// Semantic footer data — full structure ported from the marketing site's
// src/app/components/shared/footer/Footer.jsx (FooterMain) and its
// footerNavigationSchema JSON-LD.
//
// Links are stored semantically (see resolve-href.js). i18n: false marks pages
// that are not translated (category, legal) so they are never locale-prefixed.
//
// Copy is localized via a `key` on each column/item (see src/i18n/copy.js): the
// `key` is the global.json dot-path, the inline `title` is the English source +
// fallback. Key strings must be reconciled against the live global.json
// (`npm run gen:locales` reports misses); EN renders correctly regardless.
//
// `title` vs `jsonLdName`: www deliberately renders a SHORT visible label and a
// LONGER structured-data name for the same link — the footer reads "App
// Security" / "About" / "Academy" while its SiteNavigationElement says "App
// Security APIs" / "About Postman" / "Postman Academy". So `title` is the
// visible English label (and MUST equal the global.json value for `key`, since
// that is what the translated bundles carry), and `jsonLdName` is the SEO name,
// present only where the two differ.
//
// Getting this wrong is invisible in English until you diff against www: up to
// v1.0.8 `title` held the long SEO form, so EN rendered "App Security APIs"
// while JA — which reads the bundle, not the fallback — correctly rendered the
// short "アプリケーションセキュリティ". The two locales disagreed about what the
// footer says.

const internal = (path, i18n = true) => ({ type: 'internal', path, i18n });
const external = (url, target) => ({ type: 'external', url, ...(target ? { target } : {}) });

export const footerColumns = [
  {
    id: 'product',
    key: 'footer.section.product',
    title: 'Product',
    items: [
      {
        key: 'footer.link.product.postmanOverview',
        title: 'Postman Overview',
        link: internal('/product/'),
      },
      {
        key: 'footer.link.product.enterprise',
        title: 'Enterprise',
        link: internal('/postman-enterprise/'),
      },
      {
        key: 'footer.link.product.specHub',
        title: 'Spec Hub',
        link: internal('/product/spec-hub/'),
      },
      { key: 'footer.link.product.flows', title: 'Flows', link: internal('/product/flows/') },
      {
        key: 'footer.link.product.agentMode',
        title: 'Agent Mode',
        link: internal('/product/agent-mode/'),
      },
      {
        key: 'footer.link.product.apiCatalog',
        title: 'API Catalog',
        link: internal('/product/api-catalog/'),
      },
      {
        key: 'footer.link.product.fern',
        title: 'Fern',
        link: external('https://buildwithfern.com/'),
      },
      {
        key: 'footer.link.product.postmanCli',
        title: 'Postman CLI',
        // Deep-links the "work with Postman in your existing tools" section, as
        // the marketing-site footer does. Dropping the fragment landed people at
        // the top of /downloads/ instead. The locale prefix still applies —
        // localizePath prefixes the whole string, giving /jp/downloads/#…
        link: internal('/downloads/#work-with-postman-in-your-existing-tools'),
      },
      {
        key: 'footer.link.product.integrations',
        title: 'Integrations',
        link: internal('/product/integrations/'),
      },
      {
        key: 'footer.link.product.workspaces',
        title: 'Workspaces',
        link: internal('/product/workspaces/'),
      },
      {
        key: 'footer.link.product.plansAndPricing',
        title: 'Plans and Pricing',
        link: internal('/pricing/'),
      },
    ],
  },
  {
    id: 'api-network',
    key: 'footer.section.apiNetwork',
    title: 'API Network',
    items: [
      {
        key: 'footer.link.apiNetwork.appSecurity',
        title: 'App Security',
        jsonLdName: 'App Security APIs',
        link: internal('/category/app-security/', false),
      },
      {
        key: 'footer.link.apiNetwork.artificialIntelligence',
        title: 'Artificial Intelligence',
        jsonLdName: 'Artificial Intelligence APIs',
        link: internal('/category/artificial-intelligence/', false),
      },
      {
        key: 'footer.link.apiNetwork.communication',
        title: 'Communication',
        jsonLdName: 'Communication APIs',
        link: internal('/category/communication/', false),
      },
      {
        key: 'footer.link.apiNetwork.dataAnalytics',
        title: 'Data Analytics',
        jsonLdName: 'Data Analytics APIs',
        link: internal('/category/data-analytics/', false),
      },
      {
        key: 'footer.link.apiNetwork.database',
        title: 'Database',
        jsonLdName: 'Database APIs',
        link: internal('/category/database-apis/', false),
      },
      {
        key: 'footer.link.apiNetwork.developerProductivity',
        title: 'Developer Productivity',
        jsonLdName: 'Developer Productivity APIs',
        link: internal('/category/developer-productivity/', false),
      },
      {
        key: 'footer.link.apiNetwork.devops',
        title: 'DevOps',
        jsonLdName: 'DevOps APIs',
        link: internal('/category/devops/', false),
      },
      {
        key: 'footer.link.apiNetwork.eCommerce',
        title: 'E-Commerce',
        jsonLdName: 'E-Commerce APIs',
        link: internal('/category/e-commerce-apis/', false),
      },
      {
        key: 'footer.link.apiNetwork.eSignature',
        title: 'eSignature',
        jsonLdName: 'eSignature APIs',
        link: internal('/category/e-signature-apis/', false),
      },
      {
        key: 'footer.link.apiNetwork.financialServices',
        title: 'Financial Services',
        jsonLdName: 'Financial Services APIs',
        link: internal('/category/financial-services/', false),
      },
      {
        key: 'footer.link.apiNetwork.payments',
        title: 'Payments',
        jsonLdName: 'Payments APIs',
        link: internal('/category/payments/', false),
      },
      {
        key: 'footer.link.apiNetwork.travel',
        title: 'Travel',
        jsonLdName: 'Travel APIs',
        link: internal('/category/travel-apis/', false),
      },
    ],
  },
  {
    id: 'resources',
    key: 'footer.section.resources',
    title: 'Resources',
    items: [
      {
        key: 'footer.link.resources.postmanDocs',
        title: 'Postman Docs',
        link: external('https://learning.postman.com/docs/introduction/overview/'),
      },
      {
        key: 'footer.link.resources.academy',
        title: 'Academy',
        jsonLdName: 'Postman Academy',
        link: external('https://academy.postman.com/'),
      },
      {
        key: 'footer.link.resources.community',
        title: 'Community',
        jsonLdName: 'Postman Community',
        link: external('https://community.postman.com/'),
      },
      {
        key: 'footer.link.resources.intergalactic',
        title: 'Intergalactic',
        jsonLdName: 'Intergalactic Events',
        link: internal('/events/intergalactic/'),
      },
      {
        key: 'footer.link.resources.videos',
        title: 'Videos',
        link: external('https://www.youtube.com/postman/'),
      },
      {
        key: 'footer.link.resources.mcpServers',
        title: 'MCP Servers',
        link: internal('/explore/mcp-servers', false),
      },
    ],
  },
  {
    id: 'legal',
    key: 'footer.section.legalAndSecurity',
    title: 'Legal and Security',
    items: [
      {
        key: 'footer.link.legalSecurity.legalTermsHub',
        title: 'Legal Terms Hub',
        link: internal('/legal/', false),
      },
      {
        key: 'footer.link.legalSecurity.termsOfService',
        title: 'Terms of Service',
        link: internal('/legal/terms/', false),
      },
      {
        key: 'footer.link.legalSecurity.postmanProductTerms',
        title: 'Postman Product Terms',
        link: internal('/legal/postman-product-terms/', false),
      },
      {
        key: 'footer.link.legalSecurity.security',
        title: 'Security',
        link: internal('/security/'),
      },
      {
        key: 'footer.link.legalSecurity.websiteTermsOfUse',
        title: 'Website Terms of Use',
        link: internal('/legal/postman-website-terms-of-use/', false),
      },
    ],
  },
  {
    id: 'company',
    key: 'footer.section.company',
    title: 'Company',
    items: [
      {
        key: 'footer.link.company.about',
        title: 'About',
        jsonLdName: 'About Postman',
        link: internal('/company/about-postman/'),
      },
      {
        key: 'footer.link.company.careersAndCulture',
        title: 'Careers and Culture',
        link: internal('/company/careers/'),
      },
      {
        key: 'footer.link.company.contactUs',
        title: 'Contact Us',
        link: internal('/company/contact-us/'),
      },
      {
        key: 'footer.link.company.partnerProgram',
        title: 'Partner Program',
        link: internal('/partner-program/'),
      },
      {
        key: 'footer.link.company.customerStories',
        title: 'Customer Stories',
        link: internal('/customer-stories/'),
      },
      {
        key: 'footer.link.company.pressAndMedia',
        title: 'Press and Media',
        link: internal('/company/press-media/'),
      },
      { key: 'footer.links.company.events', title: 'Events', link: internal('/events/') },
    ],
  },
];

export const footerSocial = [
  {
    label: 'X',
    url: 'https://x.com/getpostman',
    icon: 'https://voyager.postman.com/logo/external/x-social-icon-black.svg',
  },
  {
    label: 'LinkedIn',
    url: 'https://www.linkedin.com/company/postman-platform',
    icon: 'https://voyager.postman.com/logo/external/lnkdin-icon.svg',
  },
  {
    label: 'GitHub',
    url: 'https://github.com/postmanlabs',
    icon: 'https://voyager.postman.com/logo/external/github-icon.svg',
  },
  {
    label: 'YouTube',
    url: 'https://www.youtube.com/postman',
    icon: 'https://voyager.postman.com/logo/external/yt-icon.svg',
  },
  {
    label: 'Instagram',
    url: 'https://www.instagram.com/getpostman',
    icon: 'https://voyager.postman.com/logo/external/instagram-logo.svg',
  },
  {
    label: 'Discord',
    url: 'https://discord.gg/58aNNsRBGR',
    icon: 'https://voyager.postman.com/logo/external/discord-mark-blue.svg',
  },
];

// Languages offered by the built-in switcher, in www's order. Endonyms are
// deliberately NOT localized: a reader looking for Japanese scans for 日本語,
// whatever the current locale is.
//
// `path` is the locale's site root, used only as the FALLBACK destination on
// foreign hosts. On the www family the switcher links to the current page's
// translation instead — see _languageHref in postman-footer.js.
//
// Keep this in step with LOCALE_PREFIX in resolve-href.js. That map also knows
// `es` and `fr-CA`; www's config carries them too but flags both
// `hiddenInSwitcher`, so surfacing them here would advertise a choice www does
// not offer.
export const footerLanguages = [
  { locale: 'en', label: 'English', path: '/' },
  { locale: 'ja', label: '日本語', path: '/jp/' },
];

// Bottom-bar labels are localizable too, so `download`/`privacyPolicy` are
// full items ({ key, title, link }) rather than bare links.
export const footerBottom = {
  logo: 'https://voyager.postman.com/logo/postman-logo-orange-stacked.svg',
  download: {
    key: 'footer.link.downloadPostman',
    title: 'Download Postman',
    link: internal('/downloads/'),
  },
  privacyPolicy: {
    key: 'footer.link.privacyPolicy',
    title: 'Privacy Policy',
    link: external('https://privacy.postman.com/policies/'),
  },
};
